import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";

const toggle = (d, idx, val) => ({ ...d, items: { ...d.items, [idx]: d.items?.[idx] === val ? null : val } });
const withNote = (d, idx, note, field) => ({ ...d, [field]: { ...(d[field] || {}), [idx]: note } });
const EMPTY_DOMAIN = { items: {}, actions: {}, evidence: {} };
const EMPTY_RESIDENT = { label: "", answers: {} };

export function useEvidenceChecklist() {
  const [data, setData] = useState({ tpl: [], areas: [], keyDocsTpl: [], resDomains: [], sampleSize: 5, sampleInfo: {} });
  const [extra, setExtra] = useState(0);
  const [practice, setPractice] = useState({});
  const [overview, setOverview] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [residents, setResidents] = useState({});
  const [plan, setPlan] = useState({});
  const [keyDocs, setKeyDocs] = useState({});
  const [score, setScore] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [assurance, setAssurance] = useState({});
  const [monthlyDone, setMonthlyDone] = useState(null);
  const [monthlyCount, setMonthlyCount] = useState(0);
  const [rounds, setRounds] = useState({ current: null, closed: [], carried: [] });
  const [startingRound, setStartingRound] = useState(false);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [docBusy, setDocBusy] = useState("");

  const dirtyRef = useRef(false);
  const load = useCallback((opts = {}) => Promise.all([api.get("/compliance/checklist"), api.get("/compliance/residents/timeline").catch(() => ({ data: [] }))]).then(([r, t]) => {
    setTimeline(t.data || []);
    const d = r.data;
    setData({ tpl: d.template, areas: d.practice_areas || [], keyDocsTpl: d.key_documents_template || [], resDomains: d.resident_domains || [], sampleSize: d.resident_sample_size || 5, sampleInfo: d.sample_info || {} });
    setOverview(d.overview || null); setScore(d.score); setKeyDocs(d.key_documents || {}); setAssurance(d.assurance || {}); setMonthlyDone(!!d.monthly_audit_done); setMonthlyCount(d.monthly_audited_count || 0);
    setRounds({ current: d.current_round || null, closed: d.audit_rounds || [], carried: d.carried_findings || [] });
    if (opts.keepEdits || dirtyRef.current) return;  // never overwrite unsaved edits (records refresh, or a late duplicate response)
    setExtra(d.sample_info?.extra || 0);
    setPractice(d.practice || {});
    setResidents(d.residents || {}); setPlan(d.action_plan || {});
    setLoadError(null);
  }).catch((e) => setLoadError(e.response?.status === 401 ? "Your session has expired — please log in again." : apiErr(e.response?.data?.detail) || "We couldn't load your assurance data.")), []);
  useEffect(() => { load(); }, [load]);
  const refreshDerived = useCallback(() => load({ keepEdits: true }), [load]);

  const completingRef = useRef(false);
  const touch = (fn) => { if (completingRef.current) { toast.info("Please wait — the review is being signed off."); return; } fn(); setDirty(true); };
  useEffect(() => { dirtyRef.current = dirty; }, [dirty]);

  const addResident = () => touch(() => { setExtra((e) => e + 1); setData((d) => ({ ...d, sampleSize: Math.min(d.sampleInfo.max || 15, d.sampleSize + 1) })); });
  const setPracticeField = (key, field, value) => touch(() => setPractice((p) => ({ ...p, [key]: { ...(p[key] || {}), [field]: value } })));

  const updateResident = (k, fn) => touch(() => setResidents((rs) => { const r = rs[k] || EMPTY_RESIDENT; return { ...rs, [k]: fn(r) }; }));
  const setResItem = (k, key, idx, val) => updateResident(k, (r) => ({ ...r, answers: { ...r.answers, [key]: toggle(r.answers[key] || EMPTY_DOMAIN, idx, val) } }));
  const setResAction = (k, key, idx, note, field = "actions") => updateResident(k, (r) => ({ ...r, answers: { ...r.answers, [key]: withNote(r.answers[key] || EMPTY_DOMAIN, idx, note, field) } }));
  const setResField = (k, field, value) => updateResident(k, (r) => ({ ...r, [field]: value }));
  const setPlanField = (key, field, value) => touch(() => setPlan((pl) => ({ ...pl, [key]: { ...(pl[key] || {}), [field]: value } })));

  const payload = { practice, residents, action_plan: plan, extra_residents: extra };
  const save = async () => {
    setSaving(true);
    try { const { data: d } = await api.post("/compliance/checklist", payload); setScore(d.score); setDirty(false); setMonthlyDone(!!d.monthly_audit_done); setMonthlyCount(d.monthly_audited_count || 0); toast.success("Evidence saved"); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const completeAssessment = async () => {
    setCompleting(true); completingRef.current = true;
    try {
      await api.post("/compliance/checklist", payload);
      const { data: d } = await api.post("/compliance/complete-assessment");
      setScore(d.score); setAssurance(d.assurance || {}); setDirty(false);
      toast.success("Quarterly review approved and signed off");
      (d.warnings || []).forEach((w) => toast.warning(w, { duration: 8000 }));
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.issues) toast.error(detail.message, { description: detail.issues.slice(0, 6).join("\n") + (detail.issues.length > 6 ? `\n…and ${detail.issues.length - 6} more` : ""), duration: 12000 });
      else toast.error(apiErr(detail));
    }
    finally { setCompleting(false); completingRef.current = false; }
  };

  const startRound = async (confirmIncomplete) => {
    if (dirtyRef.current) { toast.info("Save your progress before starting a new round."); return; }
    setStartingRound(true);
    try {
      const { data: d } = await api.post("/compliance/audit-round/start", { confirm_incomplete: !!confirmIncomplete });
      toast.success(`Round ${d.archived.round_no} archived as a read-only record · Round ${d.current_round} started`);
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail?.message || apiErr(detail));
    } finally { setStartingRound(false); }
  };

  const uploadKeyDoc = async (docKey, fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    setDocBusy(docKey);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_key", docKey);
    try {
      const { data: d } = await api.post("/compliance/key-document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setKeyDocs((k) => ({ ...k, [docKey]: d }));
      toast.success("Document uploaded");
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setDocBusy(""); }
  };
  const removeKeyDoc = async (docKey) => {
    try {
      await api.post("/compliance/key-document-delete", { doc_key: docKey });
      setKeyDocs((k) => { const c = { ...k }; delete c[docKey]; return c; });
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  return { ...data, reload: load, refreshDerived, loadError, timeline, extra, addResident, practice, overview, residents, plan, keyDocs, score, assurance, monthlyDone, monthlyCount, saving, completing, dirty, docBusy,
           currentRound: rounds.current, closedRounds: rounds.closed, carriedFindings: rounds.carried, startRound, startingRound,
           setPracticeField, setResItem, setResAction, setResField, setPlanField, save, completeAssessment, uploadKeyDoc, removeKeyDoc };
}
